import axios from 'axios';

import { assertClientSide, webpack } from '@dr.pogodin/react-utils';

import type * as TusClientServiceM from 'client/services/tus';

import type {
  CreatePractitionerBodyT,
  CreatePractitionerResT,
  GetArtistQueryT,
  GetArtistResT,
  ListArtistsQueryT,
  ListArtistsResT,
  UpdateArtistBodyT,
  UpdateArtistResT,
  UploadDocumentResT,
  UploadIntroVideoBodyT,
  UploadIntroVideoResT,
} from 'server/controllers/artists';

import type * as SSNS from 'server/services/artists';

import {
  type ArtistT,
  type AssetT,
  E_DOCUMENTS,
  E_INTRO_VIDEO,
  E_VIDEO,
  P_API_ARTIST,
  getConfig,
} from 'utils';

export * from './gallery';
export * from './profileHeroImage';

const ss = webpack.requireWeak<typeof SSNS>('server/services/artists', import.meta.dirname);

const BASE = P_API_ARTIST;

/**
 * Creates a new artist (possibly promoting a registered user to artist).
 * Client-side only!
 */
export async function createArtist(
  arg: Omit<SSNS.CreateArtistArgT, 'passwordResetUrl'>,
): Promise<SSNS.CreateArtistResT> {
  const config = await getConfig();

  const res = await axios.post<CreatePractitionerResT>(BASE, {
    _csrf: config.CSRF,
    email: arg.email,
    firstName: arg.firstName,
    lastName: arg.lastName,
    token: '',
  } satisfies CreatePractitionerBodyT);

  if (!('artist' in res.data)) throw Error('Internal error');

  return res.data;
}

export async function deleteArtist(id: string): Promise<void> {
  const config = await getConfig();
  await axios.delete(`${BASE}/${id}`, {
    headers: { 'csrf-token': config.CSRF },
  });
}

type GetArtistOptionsT = {
  asEndUser?: boolean;
  withAllUserDetails?: boolean;
};

/**
 * Gets artist object by ID. Throws if not found (unlike the underlying
 * server-side function).
 */
export async function getArtist(
  id: string,
  { asEndUser, withAllUserDetails }: GetArtistOptionsT = {},
): Promise<ArtistT> {
  if (ss) {
    const res = await ss.getArtist(id, {
      withAllUserDetails: asEndUser ? undefined : withAllUserDetails,
    });
    if (!res) throw Error('Artist object not found');
    return res;
  }

  const res = await axios<GetArtistResT>(`${BASE}/${id}`, {
    params: { asEndUser, withAllUserDetails } satisfies GetArtistQueryT,
  });

  return res.data;
}

export type ListArtistsOptionsT = ListArtistsQueryT;

/**
 * Gets the list of artists registered in the system.
 */
export async function listAritsts(
  options?: ListArtistsOptionsT,
): Promise<ListArtistsResT> {
  if (ss) {
    return {
      artists: await ss.listArtists(options),
      totalCount: options?.getTotalCount
        ? await ss.countArtists(options) : undefined,
    };
  }

  const res = await axios<ListArtistsResT>(BASE, {
    params: options satisfies ListArtistsQueryT | undefined,
  });

  return res.data;
}

export async function updateArtist(
  id: string,
  update: Partial<ArtistT> & {
    status?: 'Active' | 'Inactive';
  },
): Promise<UpdateArtistResT> {
  if (ss) throw Error('Client-side only');

  const config = await getConfig();

  const res = await axios.patch<UpdateArtistResT>(`${BASE}/${id}`, {
    _csrf: config.CSRF,
    ...update,
  } satisfies UpdateArtistBodyT);

  return res.data;
}

export async function toggleArtistStatus(
  id: string,
): Promise<ArtistT> {
  if (ss) throw Error('Client-side only');

  const config = await getConfig();

  const res = await axios.post<ArtistT>(`${BASE}/${id}/toggle-status`, {
    _csrf: config.CSRF,
  });

  return res.data;
}

type UploadDocumentArgT = {
  document: File;
  practitionerId: string;
};

export async function uploadDocument({
  document,
  practitionerId,
}: UploadDocumentArgT): Promise<AssetT> {
  assertClientSide();

  const config = await getConfig();
  const url = `${P_API_ARTIST}/${practitionerId}${E_DOCUMENTS}/${document.name || 'untitled'}`;

  const res = await axios.post<UploadDocumentResT>(url, document, {
    headers: {
      'Content-Type': document.type,
      'csrf-token': config.CSRF,
    },
  });

  return res.data;
}

type UploadIntroVideoArgT = {
  file: File;
  onProgress?: (progress: number) => void;
  practitionerId: string;
};

export async function uploadIntroVideo({
  file,
  onProgress,
  practitionerId,
}: UploadIntroVideoArgT): Promise<void> {
  assertClientSide();

  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { tusUpload } = require('client/services/tus') as typeof TusClientServiceM;

  const config = await getConfig();
  const url = `${P_API_ARTIST}/${practitionerId}${E_INTRO_VIDEO}`;

  const res = await axios.post<UploadIntroVideoResT>(url, {
    _csrf: config.CSRF,
    size: file.size,
  } satisfies UploadIntroVideoBodyT);

  if (!res.data) throw Error('Internal error');

  await tusUpload({ data: file, onProgress, url: res.data });
}

export async function deleteIntroVideo(practitionerId: string): Promise<void> {
  const config = await getConfig();
  await axios.delete(`${P_API_ARTIST}/${practitionerId}${E_INTRO_VIDEO}`, {
    headers: { 'csrf-token': config.CSRF },
  });
}

type UploadVideoArgT = UploadIntroVideoArgT;

export async function uploadVideo({
  file,
  onProgress,
  practitionerId,
}: UploadVideoArgT): Promise<void> {
  assertClientSide();

  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { tusUpload } = require('client/services/tus') as typeof TusClientServiceM;

  const url = `${P_API_ARTIST}/${practitionerId}${E_VIDEO}`;

  const config = await getConfig();

  const res = await axios.post<UploadIntroVideoResT>(url, {
    _csrf: config.CSRF,
    name: file.name,
    size: file.size,
  } satisfies UploadIntroVideoBodyT);

  if (!res.data) throw Error('Internal error');

  await tusUpload({ data: file, onProgress, url: res.data });
}
